import { defineStore } from 'pinia'
import uniq from 'lodash/uniq'
import xor from 'lodash/xor'
import { distances, useLocationSearchStore } from '~/store/location-search'
import { useCacheStore } from '~/store/cache'

const smallestDistance = distances[0]

export const useCollectionPageStore = defineStore('collection-page', () => {
  const locationSearchStore = useLocationSearchStore()
  const cacheStore = useCacheStore()
  const graphqlFetch = useGraphqlFetch()

  const state = {
    // Search context
    distance: ref(smallestDistance),
    location: ref(''),

    // Location search results
    city: ref(undefined),
    errors: ref([]),
    lat: ref(undefined),
    lng: ref(undefined),
    type: ref(undefined),

    // Selected collection districts
    selectedCollectionDistricts: ref([]),

    // Results
    collectionDistricts: ref([]),
    hits: ref([]),
    totalHits: ref(0),
  }
  state.urlParameters = computed(() => ({
    distance: state.distance.value === smallestDistance ? undefined : state.distance.value,
    location: state.location.value ? state.location.value : undefined,
  }))
  state.getCollectionDistricts = computed(() =>
    uniq([...state.collectionDistricts.value, ...state.selectedCollectionDistricts.value]).map((title) => ({
      title,
      isSelected: state.selectedCollectionDistricts.value.includes(title),
    })),
  )

  function initialize({ distance, location }: { distance?: number; location?: string }) {
    state.distance.value = distance || smallestDistance
    state.location.value = location || ''
  }

  function setDistance(distance?: number) {
    state.distance.value = distance || smallestDistance
    fetchCollectionDistricts()
  }

  function setLocation(location?: string) {
    state.location.value = location || ''
    fetchCollectionDistricts()
  }

  // Location search results
  function setLocationSearchResults({ city, errors, lat, lng, type }) {
    state.city.value = city
    state.errors.value = errors
    state.lat.value = lat
    state.lng.value = lng
    state.type.value = type
  }

  // Collection district context
  function toggleCollectionDistrict(title: string) {
    state.selectedCollectionDistricts.value = xor(state.selectedCollectionDistricts.value, [title])
  }

  // Results
  function setCollectionDistricts(collectionDistricts: any) {
    state.collectionDistricts.value = collectionDistricts
  }

  function setHits(hits: any) {
    state.hits.value = hits
  }

  function setTotalHits(totalHits: number) {
    state.totalHits.value = totalHits
  }

  async function fetchCollectionDistricts() {
    const distance = state.distance.value
    const location = state.location.value

    const { city, errors, lat, lng, status, type } = await locationSearchStore.resolve({
      distance,
      location,
    })
    setLocationSearchResults({ city, errors, lat, lng, type })
    if (['error', 'none'].includes(status)) {
      return
    }

    const key = `department-overview;lat:${lat};lng:${lng};distance:${distance}`
    const ttl = 300

    const fetcher = async () => {
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query findCollectionDistricts($mapDistance: String, $orderBy: [String]) {
          elasticSearch(site: "default", section: "collectionDistrict", orderBy: $orderBy, mapDistance: $mapDistance) {
            hits (limit: 1024) {
              title
            }
          }
        }
      `,
        variables: {
          orderBy: status === 'ok' ? [`map POINT(${lng} ${lat}) ASC`] : ['title ASC'],
          mapDistance: status === 'ok' ? `POINT(${lng} ${lat}) ${distance}` : undefined,
        },
      })

      return {
        collectionDistricts: elasticSearch.hits.map(({ title }: { title: string }) => title).sort(),
      }
    }

    // Fetch the hits
    const { collectionDistricts } = await cacheStore.fetch({
      key,
      ttl,
      fetcher,
      fallback: { collectionDistricts: [] },
    })
    setCollectionDistricts(collectionDistricts)
    return collectionDistricts
  }

  return {
    ...state,
    initialize,
    setDistance,
    setLocation,
    setLocationSearchResults,
    toggleCollectionDistrict,
    setCollectionDistricts,
    setHits,
    setTotalHits,
    fetch: fetchCollectionDistricts,
  }
})
